import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import {
  DataTable
} from "../components/ui/data-table";
import {
  TableBody,
  TableRow,
  TableHead,
  TableHeader,
} from "../components/ui/table";
import {
  UserPlus,
  Search,
  FileText,
  FilePlus,
  Eye,
  Edit,
  Trash2,
  CheckCircle2,
  CopyIcon,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../components/layout/Header";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";
import { useToast } from "../components/ui/use-toast";
import { app } from "../firebase";
import { getAuth } from "firebase/auth";
import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  getFirestore,
  onSnapshot,
  setDoc,
  Timestamp,
  updateDoc,
} from "firebase/firestore";
import { Switch } from "../components/ui/switch";
import { Label } from "../components/ui/label";
import { ColumnDef } from "@tanstack/react-table";

const STUDENTS_DATA = [
  {
    id: "ST00123",
    name: "John Doe",
    email: "john.doe@example.com",
    course: "Full Stack Web Development",
    joinDate: "2023-09-15",
    status: "Active",
    phone: "+91 9876543210",
  },
  {
    id: "ST00124",
    name: "Jane Smith",
    email: "jane.smith@example.com",
    course: "Python Developer",
    joinDate: "2023-08-22",
    status: "Active",
    phone: "+91 8765432109",
  },
  {
    id: "ST00125",
    name: "Michael Johnson",
    email: "michael.j@example.com",
    course: "Java Full Stack",
    joinDate: "2023-07-10",
    status: "Inactive",
    phone: "+91 7654321098",
  },
  {
    id: "ST00126",
    name: "Sarah Williams",
    email: "sarah.w@example.com",
    course: "React Native",
    joinDate: "2023-09-05",
    status: "Active",
    phone: "+91 6543210987",
  },
  {
    id: "ST00127",
    name: "Robert Brown",
    email: "robert.b@example.com",
    course: "UI/UX Design",
    joinDate: "2023-08-18",
    status: "Active",
    phone: "+91 5432109876",
  },
];

const Students = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const navigate = useNavigate();
  const { toast } = useToast();
  const [viewingStudent, setViewingStudent] = useState<any | null>(null);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [students, setStudents] = useState<any[]>([]);
  const [courses, setCourses] = useState([]);
  const [isCopied, setIsCopied] = useState(false);
  const db = getFirestore(app);
  const auth = getAuth(app);

  const studentsPerPage = 12;

  const filteredStudents = students.filter(
    (student) =>
      student?.studentsName?.toLowerCase().includes(searchQuery?.toLowerCase()) ||
      student?.emailId?.toLowerCase().includes(searchQuery?.toLowerCase()) ||
      student?.studentIdNo?.toLowerCase().includes(searchQuery?.toLowerCase())
  );

  // Table columns definition  
  const columns: ColumnDef<any>[] = [
    {
      accessorKey: "studentIdNo",
      header: "Student ID",
    },
    {
      accessorKey: "studentsName",
      header: "Name",
    },
    {
      accessorKey: "emailId",
      header: "Email",
    },
    {
      accessorKey: "mobileNumber",
      header: "Phone",
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ row }) => {
        const status = row.getValue('status') as string;
        return (
          <Badge variant={status === 'Active' ? 'success' : 'destructive'}>
            {status}
          </Badge>
        );
      },
    },
    {
      accessorKey: "actions",
      header: "Actions",
      cell: ({ row }) => {
        const student = row.original;
        return (
          <div className="flex justify-end gap-1">
            <Button size="sm" variant="ghost" onClick={() => handleViewStudent(student)}>
              <Eye className="h-4 w-4" />
            </Button>
            <Button size="sm" variant="ghost" onClick={() => handleEditStudent(student)}>
              <Edit className="h-4 w-4" />
            </Button>
          </div>
        );
      },
    },
  ];

  const handleCopyLink = () => {
    const draftLink = "https://registration.bytesplash.in/students/draft-register";
    navigator.clipboard.writeText(draftLink)
      .then(() => {
        setIsCopied(true);
        toast({
          title: "Copied",
          description: "Draft link copied to clipboard."
        });

        setTimeout(() => {
          setIsCopied(false);
        }, 2000);
      })
      .catch(err => {
        toast({
          title: "Error",
          description: "Failed to copy link.",
          variant: "destructive",
        });
      });
  };

  const getCourseName = (courseId: any) => {
    return courses?.find(c => c.id == courseId)?.courseName;
  }

  const handleViewStudent = (student: any) => {
    setViewingStudent(student);
    setIsViewDialogOpen(true);
  };

  const handleEditStudent = (student: any) => {
    navigate(`/students/register?id=${student.id}`);
    toast({
      title: "Editing Student",
      description: `You are now editing ${student.name}'s information.`,
    });
  };

  useEffect(() => {
    const coursesUnsubscribe = onSnapshot(
      collection(db, "courses"),
      (querySnapshot) => {
        const result = querySnapshot.docs.map((doc) => {
          return {
            id: doc.id,
            ...doc.data()
          };
        });
        setCourses(result);
      },
      (error) => {
        toast({
          title: "Error",
          description: error.message,
          variant: "destructive",
        });
      }
    );

    const draftRegistrationsSubscribe = onSnapshot(
      collection(db, "students"),
      (querySnapshot) => {
        const result: any = querySnapshot.docs.map((doc) => {
          return {
            id: doc.id,
            ...doc.data()
          };
        }).map((x: any) => {
          return {
            ...x,
            studentIdNumber: x?.studentIdNumber ?? x.studentIdNo,
            studentIdNo: x?.studentIdNo ?? x?.studentIdNumber
          }
        });
        console.log(result);
        const internalList = result.filter(x => x.isInternal == true && ((x['studentIdNo'] || x['studentIdNumber'])));
        setStudents(internalList);
      },
      (error) => {
        toast({
          title: "Error",
          description: error.message,
          variant: "destructive",
        });
      }
    );

    return () => {
      coursesUnsubscribe();
      draftRegistrationsSubscribe();
    };
  }, []);

  return (
    <div className="h-full flex flex-col bg-gray-50">
      <Header title="Students" subtitle="Manage your students" />

      <div className="flex-1 p-6 animate-fade-in">
        <Card className="shadow-sm">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center p-6 border-b">
            <h2 className="text-xl font-semibold text-gray-800 mb-4 sm:mb-0">All Students</h2>
            <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
              <div className="relative w-full sm:w-64">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-500" />
                <Input
                  type="search"
                  placeholder="Search students..."
                  className="pl-8 w-full"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              <div className="flex gap-2">
                <Button onClick={() => navigate('/students/register')} className="w-full sm:w-auto">
                  <UserPlus className="mr-2 h-4 w-4" />
                  Register Student
                </Button>

                <Button
                  onClick={() => navigate('/students/draft-register')}
                  variant="outline"
                  className="w-full sm:w-auto"
                >
                  <FilePlus className="mr-2 h-4 w-4" />
                  Draft
                </Button>
                <Button
                  onClick={() => navigate('/students/draft-list')}
                  variant="secondary"
                  className="w-full sm:w-auto"
                >
                  <FileText className="mr-2 h-4 w-4" />
                  Draft List
                </Button>
                <Button onClick={handleCopyLink} className="w-full sm:w-auto">
                  {isCopied ? (
                    <CheckCircle2 className="mr-2 h-4 w-4 text-white" />
                  ) : (
                    <CopyIcon className="mr-2 h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>
          </div>
          <CardContent className="p-4">
            <DataTable
              columns={columns}
              data={filteredStudents}
              noResultsMessage="No students found"
            />
          </CardContent>
        </Card>
      </div>

      {/* View Student Dialog */}
      <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
        <DialogContent className="sm:max-w-md h-[95vh] overflow-auto">
          <DialogHeader>
            <DialogTitle>Student Details</DialogTitle>
            <DialogDescription>
              Detailed information about the student
            </DialogDescription>
          </DialogHeader>

          {viewingStudent && (
            <div className="grid gap-2 py-2">
              <div className="flex items-center justify-center mb-2">
                <div className="h-24 w-24 rounded-full bg-gray-200 flex items-center justify-center text-gray-500 text-2xl font-semibold">
                  {viewingStudent?.studentsName?.split(' ').map((n: string) => n[0]).join('')}
                </div>
              </div>

              <div className="bg-gray-50 p-1 rounded-lg border">
                <div className="flex justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Student ID</p>
                    <p className="font-semibold">{viewingStudent?.id}</p>
                  </div>
                  <div className="flex items-center">
                    <p className="text-sm text-gray-500 mr-2">Status</p>
                    <Badge className={viewingStudent.status === 'Active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                      {viewingStudent.status}
                    </Badge>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-1 gap-4 pt-2">
                <div>
                  <p className="text-sm text-gray-500">Email Address</p>
                  <p className="font-semibold">{viewingStudent.emailId}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4 pt-2">
                <div>
                  <p className="text-sm text-gray-500">Full Name</p>
                  <p className="font-semibold">{viewingStudent.studentsName}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Phone Number</p>
                  <p className="font-semibold">{viewingStudent.mobileNumber}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Enrolled Course</p>
                  <p className="font-semibold">{getCourseName(viewingStudent.courses[0]?.courseId)}</p>
                </div>
              </div>
              <div className="pt-4 flex justify-end">
                <Button onClick={() => handleEditStudent(viewingStudent)}>
                  <Edit className="mr-2 h-4 w-4" />
                  Edit Student
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Students;


